import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface Material {
  id: string;
  name: string;
  category: 'Seeds' | 'Tools' | 'Soil' | 'Containers' | 'Infrastructure' | 'Other';
  quantity: string;
  estimatedCost: number;
  priority: 'Essential' | 'Recommended' | 'Optional';
  supplier?: string;
  notes?: string;
}

interface MaterialsListProps {
  materials: Material[];
}

const MaterialsList = ({ materials }: MaterialsListProps) => {
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Seeds': return 'SparklesIcon';
      case 'Tools': return 'WrenchScrewdriverIcon';
      case 'Soil': return 'GlobeAmericasIcon';
      case 'Containers': return 'ArchiveBoxIcon';
      case 'Infrastructure': return 'BuildingOfficeIcon';
      default: return 'CubeIcon';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Seeds': return 'text-success bg-success/10';
      case 'Tools': return 'text-primary bg-primary/10';
      case 'Soil': return 'text-warning bg-warning/10';
      case 'Containers': return 'text-accent bg-accent/10';
      case 'Infrastructure': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Essential': return 'text-error bg-error/10 border-error/20';
      case 'Recommended': return 'text-warning bg-warning/10 border-warning/20';
      case 'Optional': return 'text-muted-foreground bg-muted border-border';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const groupedMaterials = materials.reduce((acc, material) => {
    if (!acc[material.category]) {
      acc[material.category] = [];
    }
    acc[material.category].push(material);
    return acc;
  }, {} as Record<string, Material[]>);

  const totalCost = materials.reduce((sum, material) => sum + material.estimatedCost, 0);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Materials & Supplies</h3>
        <div className="text-right">
          <div className="text-sm text-muted-foreground">Total Estimated Cost</div>
          <div className="text-xl font-bold text-foreground">${totalCost.toFixed(2)}</div>
        </div>
      </div>

      <div className="space-y-6">
        {Object.entries(groupedMaterials).map(([category, categoryMaterials]) => (
          <div key={category} className="border border-border rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className={`p-2 rounded-lg ${getCategoryColor(category)}`}>
                <Icon name={getCategoryIcon(category) as any} size={20} />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">{category}</h4>
                <p className="text-sm text-muted-foreground">
                  {categoryMaterials.length} item{categoryMaterials.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {categoryMaterials.map((material) => (
                <div key={material.id} className="flex items-center justify-between p-3 bg-surface rounded-lg border border-border">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h5 className="font-medium text-foreground">{material.name}</h5>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(material.priority)}`}>
                        {material.priority}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <Icon name="HashtagIcon" size={14} />
                        <span>Qty: {material.quantity}</span>
                      </div>
                      {material.supplier && (
                        <div className="flex items-center space-x-1">
                          <Icon name="BuildingStorefrontIcon" size={14} />
                          <span>{material.supplier}</span>
                        </div>
                      )}
                    </div>

                    {material.notes && (
                      <p className="text-xs text-muted-foreground mt-2 italic">{material.notes}</p>
                    )}
                  </div>

                  <div className="text-right ml-4">
                    <div className="text-lg font-semibold text-foreground">${material.estimatedCost.toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-surface rounded-lg border border-border">
        <div className="flex items-center space-x-2 mb-3">
          <Icon name="InformationCircleIcon" size={20} className="text-primary" />
          <h4 className="font-semibold text-foreground">Cost Breakdown</h4>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Essential:</span>
            <div className="font-semibold text-foreground">
              ${materials.filter(m => m.priority === 'Essential').reduce((sum, m) => sum + m.estimatedCost, 0).toFixed(2)}
            </div>
          </div>
          <div>
            <span className="text-muted-foreground">Recommended:</span>
            <div className="font-semibold text-foreground">
              ${materials.filter(m => m.priority === 'Recommended').reduce((sum, m) => sum + m.estimatedCost, 0).toFixed(2)}
            </div>
          </div>
          <div>
            <span className="text-muted-foreground">Optional:</span>
            <div className="font-semibold text-foreground">
              ${materials.filter(m => m.priority === 'Optional').reduce((sum, m) => sum + m.estimatedCost, 0).toFixed(2)}
            </div>
          </div>
          <div>
            <span className="text-muted-foreground">Total:</span>
            <div className="font-bold text-primary text-lg">${totalCost.toFixed(2)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialsList;